/**
 * 🚀 ADMIN PAGE V2 - SPACE EDITION
 * Panneau d'administration style vaisseau spatial
 * 
 * Architecture modulaire pour ne plus jamais perdre de fonctionnalités !
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '../services/api';

// Composants spatiaux
import SpaceLayout from '../components/admin/SpaceLayout';
import SpaceDashboard from '../components/admin/SpaceDashboard';
import SpaceCustomers from '../components/admin/SpaceCustomers';
import SpaceDelivery from '../components/admin/SpaceDelivery';
import SpaceMessaging from '../components/admin/SpaceMessaging';
import SpaceHeartbeats from '../components/admin/SpaceHeartbeats';
import SpaceVendors from '../components/admin/SpaceVendors';
import SpaceSMS from '../components/admin/SpaceSMS';
import SpaceCredits from '../components/admin/SpaceCredits';
import SpacePromos from '../components/admin/SpacePromos';
import SpaceProspects from '../components/admin/SpaceProspects';
import SpaceAnalytics from '../components/admin/SpaceAnalytics';
import SpaceExport from '../components/admin/SpaceExport';
import SpaceContent from '../components/admin/SpaceContent';
import SpaceVisitors from '../components/admin/SpaceVisitors';
import SpaceSettings from '../components/admin/SpaceSettings';
import SpaceLoyaltyCards from '../components/admin/SpaceLoyaltyCards';
import SpaceAutomation from '../components/admin/SpaceAutomation';
import { useSpaceSounds } from '../components/admin/SpaceSounds';
import { SPACE_MODULES } from '../components/admin/SpaceNav';

// UI Components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Loader2, Lock, Eye, EyeOff, Rocket } from 'lucide-react';

// ============================================
// 🔐 ÉCRAN DE LOGIN SPATIAL
// ============================================
const SpaceLogin = ({ onLogin, isLoading }) => {
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [showPin, setShowPin] = useState(false);

  const handlePinChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newPin = [...pin];
    newPin[index] = value.slice(-1);
    setPin(newPin);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      document.getElementById(`space-pin-${index + 1}`)?.focus();
    }

    // Auto-submit when complete
    if (newPin.every(d => d !== '') && newPin.join('').length === 6) {
      onLogin(newPin.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      document.getElementById(`space-pin-${index - 1}`)?.focus();
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center relative overflow-x-hidden overflow-y-auto">
      {/* Étoiles de fond */}
      {[...Array(50)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-white rounded-full"
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: [0.2, 0.8, 0.2],
            scale: [1, 1.5, 1]
          }}
          transition={{ 
            duration: 2 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2
          }}
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
        />
      ))}

      {/* Nébuleuse */}
      <div className="absolute w-96 h-96 rounded-full opacity-20 blur-3xl bg-gradient-to-br from-cyan-500 to-purple-500" 
        style={{ left: '20%', top: '20%' }} 
      />
      <div className="absolute w-64 h-64 rounded-full opacity-20 blur-3xl bg-gradient-to-br from-orange-500 to-pink-500" 
        style={{ right: '20%', bottom: '20%' }} 
      />

      {/* Card de login */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="backdrop-blur-xl rounded-3xl p-8 border border-cyan-500/30 bg-[#12121a]/90 shadow-2xl shadow-cyan-500/10">
          {/* Logo animé */}
          <motion.div 
            className="flex justify-center mb-8"
            animate={{ 
              y: [0, -10, 0],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="relative">
              <motion.div
                animate={{ 
                  boxShadow: [
                    '0 0 20px rgba(0, 212, 255, 0.3)',
                    '0 0 40px rgba(0, 212, 255, 0.6)',
                    '0 0 20px rgba(0, 212, 255, 0.3)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center"
              >
                <Rocket className="w-10 h-10 text-white" />
              </motion.div>
            </div>
          </motion.div>

          <h1 className="text-2xl font-black text-center text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-2">
            COCKPIT D'ADMINISTRATION
          </h1>
          <p className="text-gray-500 text-center text-sm mb-8">
            La Petite Épicerie - Accès sécurisé
          </p>

          {/* PIN Input */}
          <div className="flex justify-center gap-3 mb-6">
            {pin.map((digit, index) => (
              <motion.input
                key={index}
                id={`space-pin-${index}`}
                type={showPin ? 'text' : 'password'}
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`
                  w-12 h-14 text-center text-2xl font-bold rounded-xl
                  bg-[#1a1a25] border-2 text-white
                  focus:outline-none focus:ring-2 focus:ring-cyan-500/50
                  transition-all
                  ${digit ? 'border-cyan-500' : 'border-gray-700'}
                  ${error ? 'border-red-500 shake' : ''}
                `}
                maxLength={1}
                autoFocus={index === 0}
                whileFocus={{ scale: 1.05 }}
              />
            ))}
          </div>

          {/* Toggle visibility */}
          <button
            onClick={() => setShowPin(!showPin)}
            className="flex items-center justify-center gap-2 text-gray-500 text-sm mx-auto mb-6 hover:text-cyan-400 transition-colors"
          >
            {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showPin ? 'Masquer' : 'Afficher'}
          </button>

          {/* Error */}
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-400 text-sm text-center mb-4"
            >
              {error}
            </motion.p>
          )}

          {/* Submit button */}
          <Button
            onClick={() => onLogin(pin.join(''))}
            disabled={isLoading || pin.some(d => d === '')}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 h-12 text-lg font-bold shadow-lg shadow-cyan-500/30"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Lock className="w-5 h-5 mr-2" />
                Accéder au Cockpit
              </>
            )}
          </Button>

          <p className="text-gray-600 text-xs text-center mt-6">
            🔒 Connexion sécurisée • v3.0 Space Edition
          </p>
        </div>
      </motion.div>

      {/* CSS pour l'animation shake */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .shake { animation: shake 0.3s ease-in-out; }
      `}</style>
    </div>
  );
};

// ============================================
// 🚀 PAGE PRINCIPALE
// ============================================
export default function AdminPageV2() {
  // Navigation
  const navigate = useNavigate();
  
  // État d'authentification
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [adminCode, setAdminCode] = useState(null); // Code admin pour le 2FA

  // État du module actif
  const [activeModule, setActiveModule] = useState('dashboard');

  // Sons spatiaux 🔊
  const { playSound, enabled: soundsEnabled, toggleSounds } = useSpaceSounds();

  // Données
  const [stats, setStats] = useState(null);
  const [heartbeatsStats, setHeartbeatsStats] = useState({});
  const [badges, setBadges] = useState({});

  // Naviguer vers la fiche client épicier pour tamponner
  const handleSelectCustomer = useCallback((customer) => {
    if (customer?.id) {
      // Ouvrir la fiche client dans un nouvel onglet (avec le lien direct)
      // L'admin peut soit cliquer sur le lien, soit se connecter en tant qu'épicier
      const url = `/team-lpe/customer/${customer.id}`;
      
      // Stocker aussi le téléphone pour la page de recherche
      if (customer?.phone) {
        sessionStorage.setItem('selectedCustomerPhone', customer.phone);
      }
      
      // Naviguer vers la page de recherche épicier
      navigate('/team-lpe/search');
      toast.success(`📍 Ouverture de la fiche de ${customer.name || customer.firstName || 'Client'}...`, {
        description: 'Connectez-vous en tant qu\'épicier pour tamponner les cartes',
        duration: 5000
      });
    } else {
      toast.error('Impossible d\'ouvrir la fiche client');
    }
  }, [navigate]);

  // Gestion du changement de module avec son
  const handleModuleChange = useCallback(async (module) => {
    playSound('nav');
    setActiveModule(module);
    
    // Si on entre dans le module Radar (prospects), marquer comme vus
    if (module === 'prospects') {
      try {
        await api.markAllProspectsViewed?.();
        setBadges(prev => ({ ...prev, prospects: 0 }));
      } catch (e) {
        // Pas bloquant
      }
    }
    
    // Si on entre dans le module Missions (livraisons), marquer comme lues
    if (module === 'delivery') {
      try {
        // Marquer toutes les livraisons comme lues
        const deliveries = await api.getDeliveryRequests?.();
        const unreadDeliveries = (deliveries || []).filter(d => !d.read);
        for (const del of unreadDeliveries) {
          await api.markDeliveryRead?.(del.id);
        }
        setBadges(prev => ({ ...prev, delivery: 0 }));
      } catch (e) {
        // Pas bloquant
      }
    }
  }, [playSound]);

  // Charger les données
  const loadData = useCallback(async () => {
    try {
      // Stats principales
      const statsData = await api.getStats();
      setStats(statsData);

      // Badges (notifications)
      let deliveryCount = 0;
      try {
        const deliveryNotif = await api.getUnreadDeliveryCount?.();
        deliveryCount = deliveryNotif?.count || 0;
      } catch (e) {
        // Pas grave
      }
      
      // Nouveaux prospects non vus
      let prospectsCount = 0;
      try {
        const prospectsNotif = await api.getNewProspectsCount?.();
        prospectsCount = prospectsNotif?.count || 0;
      } catch (e) {
        // Pas grave
      }
      
      // 💬 Messages non lus (Comms)
      let messagingCount = 0;
      try {
        const chatStats = await api.getChatStats?.();
        messagingCount = chatStats?.totalUnread || 0;
      } catch (e) {
        // Pas grave
      }
      
      setBadges(prev => {
        // 🔔 NOTIFICATION SONORE si nouvelles livraisons depuis la dernière vérification
        if (deliveryCount > 0 && deliveryCount > (prev.delivery || 0)) {
          playSound('alert');
          toast.info(`🚚 ${deliveryCount} nouvelle(s) commande(s) de livraison !`, {
            duration: 10000,
            action: {
              label: 'Voir',
              onClick: () => setActiveModule('delivery')
            }
          });
        }
        
        // 🔔 NOTIFICATION SONORE si nouveaux messages depuis la dernière vérification
        if (messagingCount > 0 && messagingCount > (prev.messaging || 0)) {
          playSound('alert');
          toast.info(`💬 ${messagingCount} nouveau(x) message(s) !`, {
            duration: 10000,
            action: {
              label: 'Voir',
              onClick: () => setActiveModule('messaging')
            }
          });
        }
        
        return {
          delivery: deliveryCount,
          messaging: messagingCount,
          prospects: prospectsCount,
        };
      });

      // Stats Heartbeats (si disponible)
      try {
        const hbData = await api.getAdminHeartbeatsStats?.();
        setHeartbeatsStats(hbData || {});
      } catch (e) {
        // Pas grave si pas disponible
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
  }, [playSound, setActiveModule]);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
      // Refresh toutes les 30 secondes
      const interval = setInterval(loadData, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, loadData]);

  // Login
  const handleLogin = async (pin) => {
    setIsLoading(true);
    try {
      const result = await api.adminLogin(pin);
      if (result.success) {
        setIsAuthenticated(true);
        setAdminCode(pin); // Stocker le code admin pour le 2FA
        playSound('warp'); // Son de warp au login !
        toast.success('🚀 Bienvenue dans le cockpit !');
      } else if (result.requires2FA) {
        // TODO: Gérer 2FA
        toast.info('Vérification 2FA requise');
      } else {
        playSound('error');
        toast.error('Code incorrect');
      }
    } catch (error) {
      playSound('error');
      toast.error('Erreur de connexion');
    } finally {
      setIsLoading(false);
    }
  };

  // Logout
  const handleLogout = () => {
    playSound('hologram');
    setIsAuthenticated(false);
    setAdminCode(null); // Effacer le code admin
    setActiveModule('dashboard');
    toast.success('Déconnexion réussie');
  };

  // Rendu du module actif
  const renderModule = () => {
    switch (activeModule) {
      case 'dashboard':
        return (
          <SpaceDashboard
            stats={stats || {}}
            heartbeatsStats={heartbeatsStats}
            alerts={[]}
            onNavigate={handleModuleChange}
            isLoading={!stats}
          />
        );
      
      case 'customers':
        return <SpaceCustomers onSelectCustomer={handleSelectCustomer} />;
      
      case 'delivery':
        return <SpaceDelivery />;
      
      case 'messaging':
        return <SpaceMessaging />;
      
      case 'heartbeats':
        return <SpaceHeartbeats />;
      
      case 'vendors':
        return <SpaceVendors />;
      
      case 'sms':
        return <SpaceSMS />;
      
      case 'credits':
        return <SpaceCredits />;
      
      case 'promos':
        return <SpacePromos />;
      
      case 'prospects':
        return <SpaceProspects />;
      
      case 'automation':
        return <SpaceAutomation />;
      
      case 'analytics':
        return <SpaceAnalytics />;
      
      case 'export':
        return <SpaceExport />;
      
      case 'content':
        return <SpaceContent />;
      
      case 'visitors':
        return <SpaceVisitors />;
      
      // Module Settings - Module complet de paramètres avec 2FA
      case 'settings':
        return <SpaceSettings adminCode={adminCode} />;
      
      // Module Cartes de Fidélité personnalisables
      case 'loyalty-cards':
        return <SpaceLoyaltyCards />;
      
      default:
        return (
          <div className="text-center py-12">
            <p className="text-gray-500">
              Module inconnu : "{activeModule}"
            </p>
          </div>
        );
    }
  };

  // Écran de login
  if (!isAuthenticated) {
    return <SpaceLogin onLogin={handleLogin} isLoading={isLoading} />;
  }

  // Interface principale
  return (
    <SpaceLayout
      activeModule={activeModule}
      onModuleChange={handleModuleChange}
      badges={badges}
      onLogout={handleLogout}
      soundsEnabled={soundsEnabled}
      onToggleSounds={toggleSounds}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={activeModule}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {renderModule()}
        </motion.div>
      </AnimatePresence>
    </SpaceLayout>
  );
}
